import { createBrowserRouter, Link, Navigate } from 'react-router-dom';
import { AdminRoute } from './admin-route';
import i18n from './i18n/config';
import { Layout } from './layout/layout';
import { RootLayout } from './layout/root-layout';
import { Alerts } from './modules/alerts/alerts';
import ForgotPasswordPage from './modules/auth/forgot-password-page';
import LoginPage from './modules/auth/login-page';
import ResetPasswordPage from './modules/auth/reset-password-page';
import SignUpPage from './modules/auth/sign-up-page';
import VerifyEmailPage from './modules/auth/verify-email-page';
import { ChatPage } from './modules/chat2/chat-page';
import { Dashboard } from './modules/dashboard/dashboard';
import Integrations from './modules/integrations';
import { InvitationPage } from './modules/invitations/components/InvitationPage';
import PrivacyPolicy from './modules/legal/privacy-policy';
import TermsOfService from './modules/legal/terms-of-service';
import { ProjectDetail } from './modules/projects/project-detail';
import { Projects } from './modules/projects/projects-list';
import ReferenceFormPage from './modules/reference/reference-form';
import { AutoUserRedirect } from './modules/reports/auto-user-redirect';
import { EmptyUserReport } from './modules/reports/components/empty-user-report';
import UserReport from './modules/reports/user-report';
import { Profile } from './modules/settings';
import { Members } from './modules/settings/members/members';
import { Workspace } from './modules/settings/workspace';
import { Users } from './modules/users/users';
import { AutoWorkspaceRedirect } from './modules/workspace/auto-workspace-redirect';
import { ProtectedRoute } from './protected-route';
import { WorkspaceRoute } from './workspace-route';

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/invitations/:invitationId',
        element: <InvitationPage />,
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/sign-up',
        element: <SignUpPage />,
      },
      {
        path: '/auth/verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: '/auth/reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/terms',
        element: <TermsOfService />,
      },
      {
        path: '/privacy',
        element: <PrivacyPolicy />,
      },
      {
        path: '/references/form/:token',
        element: <ReferenceFormPage />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/',
            element: <AutoWorkspaceRedirect />,
          },
          {
            path: '/:workspaceId',
            element: <WorkspaceRoute />,
            children: [
              {
                element: <Layout />,
                handle: {
                  crumb: () => <Link to="/:workspaceId">{i18n.t('navigation:dashboard')}</Link>,
                },
                children: [
                  {
                    index: true,
                    element: <Dashboard />,
                  },
                  {
                    path: 'chat',
                    element: <ChatPage />,
                    handle: {
                      crumb: () => <Link to="chat">{i18n.t('navigation:chat')}</Link>,
                    },
                  },
                  {
                    path: 'integrations',
                    element: <Integrations />,
                    handle: {
                      crumb: () => <Link to="integrations">{i18n.t('navigation:integrations')}</Link>,
                    },
                  },
                  {
                    path: 'reports',
                    element: <EmptyUserReport />,
                    handle: {
                      crumb: () => i18n.t('navigation:reports'),
                    },
                  },
                  {
                    path: 'reports/:projectId',
                    element: <AutoUserRedirect />,
                    handle: {
                      crumb: () => i18n.t('navigation:reports'),
                    },
                  },
                  {
                    path: 'reports/:projectId/user/:userId',
                    element: <UserReport />,
                    handle: {
                      crumb: () => i18n.t('navigation:reports'),
                    },
                  },
                  {
                    path: 'contacts',
                    handle: {
                      crumb: () => (
                        <Link to="contacts/prioritized">{i18n.t('navigation:crumbs.clinicians')}</Link>
                      ),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to="statuses/Lead" replace />,
                      },
                    ],
                  },
                  {
                    path: 'catalogs',
                    handle: {
                      crumb: () => (
                        <Link to="catalogs/specialties">{i18n.t('navigation:crumbs.catalogs')}</Link>
                      ),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to="specialties" replace />,
                      },
                    ],
                  },
                  {
                    path: 'projects',
                    handle: {
                      crumb: () => <Link to="projects">{i18n.t('navigation:projects')}</Link>,
                    },
                    children: [
                      {
                        index: true,
                        element: <Projects />,
                      },
                      {
                        path: ':projectId',
                        element: <ProjectDetail />,
                        handle: {
                          crumb: () => i18n.t('navigation:crumbs.projectDetails'),
                        },
                      },
                    ],
                  },
                  {
                    path: 'alerts',
                    handle: {
                      crumb: () => <Link to="alerts">{i18n.t('navigation:alerts')}</Link>,
                    },
                    children: [
                      {
                        index: true,
                        element: <Alerts />,
                      },
                    ],
                  },
                  {
                    path: 'settings',
                    handle: {
                      crumb: () => <Link to="settings">{i18n.t('navigation:settings')}</Link>,
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to="profile" replace />,
                      },
                      {
                        path: 'profile',
                        element: <Profile />,
                        handle: {
                          crumb: () => i18n.t('navigation:crumbs.profileSettings'),
                        },
                      },
                      {
                        path: 'workspace',
                        element: <Workspace />,
                        handle: {
                          crumb: () => i18n.t('navigation:crumbs.workspaceSettings'),
                        },
                      },
                      {
                        path: 'members',
                        element: <Members />,
                        handle: {
                          crumb: () => i18n.t('navigation:crumbs.teamMembers'),
                        },
                      },
                    ],
                  },
                  {
                    element: <AdminRoute />,
                    children: [
                      {
                        path: 'users',
                        element: <Users />,
                        handle: {
                          crumb: () => <Link to="users">{i18n.t('navigation:crumbs.users')}</Link>,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
